import * as React from 'react';
const { CheckBox } = require('@cvent/carina-checkbox');
const { RadioGroup } = require('@cvent/carina-radio-group');

import { Search } from '@weddingspot/ws-api-client';

import { FilterSection } from './FiltersSection';
import { LocationsFilters } from './LocationsFilters';
import { StylesFilters, pubSubStylesInput } from './StylesFilters';
import { BudgetRangeFilter } from './BudgetRangeFilter';
import { GuestCountFilter } from './GuestCountFilter';

const _styles = require('./FiltersSidebar.scss');

import { isEqual, pickBy, size } from 'lodash-es';

import { useFormikContext, FormikContextType } from 'formik';
import { SRPFormikValues } from '.';
import { BudgetRangePopover } from './BudgetRangePopover';
import { AnalyticsEvent, logEvent } from '@weddingspot/ws-analytics';

interface FilterSidebarProps {
    searchFilters: Search.SearchFilters;
    selectedMarket: string;
}

export const filterDefaultValuesGroup = {
    location: {
        region: {},
        market: {}
    },
    styles: {
        styles: {},
    },
    other: {
        budget: [0, null],
        guestCount: [0, null],
        byo: false,
        sr: 1,
        bbox: null,
    },
    budget: { budget: [0, null] },
    guests: { guestCount: [0, null] },
    byo: { byo: false },
    services: { sr: 1 },
};

export const resetAllFilter = (
    formikContext: FormikContextType<SRPFormikValues>,
    subgroups: Array<keyof typeof filterDefaultValuesGroup> = ['location', 'styles', 'other']
) => {
    const { setFieldValue, setFieldTouched } = formikContext;
    setTimeout(() => {
        subgroups.forEach((groupName) => {
            for (const [key, value] of Object.entries(filterDefaultValuesGroup[groupName])) {
                setFieldValue(key, value);
            }
            setFieldTouched(groupName, true);
        });
    });
};

export const FilterSidebar = (props: FilterSidebarProps) => {
    const { locations, styles, budget, guests, services, byo } = props.searchFilters;
    const selectedMarket = props.selectedMarket;

    const formikContext = useFormikContext<SRPFormikValues>();
    const { values, setFieldValue, setFieldTouched, handleChange } = formikContext;

    const possibleResetFilter =
        size(pickBy(values.market)) ||
        size(pickBy(values.region)) ||
        size(pickBy(values.styles)) ||
        !isEqual(values.budget, [0, null]) ||
        !isEqual(values.guestCount, [0, null]) ||
        values.byo ||
        values.sr !== 1;

    return (
        <div className={_styles.wrapper}>
            <button
                disabled={!possibleResetFilter}
                className={_styles.resetButton}
                onClick={() => {
                    resetAllFilter(formikContext);
                    pubSubStylesInput.fire('resetFilter');
                }}
            >
                Reset All Filters
            </button>

            {locations && (
                <FilterSection
                    sectionTitle='Locations'
                    badgeCount={size(pickBy(values.region)) + size(pickBy(values.market))}
                    defaultExpanded={true}
                    clearable={true}
                    clearCallback={() => {
                        logEvent(AnalyticsEvent.EVTNM_CLICKED_RESET_FILTERS, { Type: 'Regions', 'SRP Version': 2 });
                        resetAllFilter(formikContext, ['location']);
                    }}
                >
                    <LocationsFilters locations={locations} collapsibleSubsections={true} />
                </FilterSection>
            )}
            {styles && (
                <FilterSection
                    sectionTitle='Venue styles'
                    badgeCount={size(pickBy(values.styles))}
                    defaultExpanded={true}
                    clearable={true}
                    clearCallback={() => {
                        logEvent(AnalyticsEvent.EVTNM_CLICKED_RESET_FILTERS, { Type: 'Styles', 'SRP Version': 2 });
                        resetAllFilter(formikContext, ['styles']);
                    }}
                >
                    <StylesFilters styles={styles} collapsibleSubsections={true} />
                </FilterSection>
            )}
            {budget && (
                <FilterSection
                    sectionTitle={
                        <div className={_styles.budgetFilterHeader}>
                            <div className={_styles.budgetFilterHeaderText}>Budget</div>
                            <BudgetRangePopover webInteraction={true} />
                        </div>
                    }
                    defaultExpanded={true}
                >
                    {({ expanded }) => (
                        <BudgetRangeFilter
                            isVisible={expanded}
                            min={budget.min}
                            max={budget.max}
                            defaultValue={budget.default}
                            onChange={(values) => {
                                setFieldValue('budget', values);
                                setFieldTouched('budget', true);
                            }}
                        />
                    )}
                </FilterSection>
            )}
            {guests && (
                <FilterSection sectionTitle='Guests' defaultExpanded={true}>
                    {({ expanded }) => (
                        <GuestCountFilter
                            isVisible={expanded}
                            min={guests.min}
                            max={guests.max}
                            defaultValue={guests.default}
                            onChange={(values) => {
                                setFieldValue('guestCount', values);
                                setFieldTouched('guestCount', true);
                            }}
                        />
                    )}
                </FilterSection>
            )}
            {services && (
                <FilterSection sectionTitle='Services' defaultExpanded={true}>
                    <div className={_styles.servicesRadiogroupContainer}>
                        <RadioGroup
                            name='sr'
                            options={services.options}
                            selected={values.sr}
                            onUpdate={(value: string, radio: any) => {
                                setTimeout(() => {
                                    setFieldValue('sr', value);
                                    setFieldTouched('sr', true);
                                    logEvent(AnalyticsEvent.EVTNM_CLICKED_FILTER, {
                                        'Current Market': selectedMarket,
                                        Type: 'Services',
                                        Label: radio.label,
                                        Value: value,
                                        Checked: values.sr,
                                        'SRP Version': 2,
                                    });
                                });
                            }}
                        />
                    </div>

                    {services.links && (
                        <div className={_styles.servicesLinks}>
                            {services.links.map((l, idx) => (
                                <a href={l.url} key={idx}>
                                    {l.label}
                                </a>
                            ))}
                        </div>
                    )}
                </FilterSection>
            )}
            {byo && (
                <FilterSection sectionTitle='Alcohol &amp; Beverages' defaultExpanded={true}>
                    <CheckBox
                        name='byo'
                        id='byo'
                        checked={values.byo}
                        onChange={(e: React.ChangeEvent) => {
                            handleChange(e);
                            logEvent(AnalyticsEvent.EVTNM_CLICKED_FILTER, {
                                Type: 'Regions',
                                Label: 'Allows BYO',
                                Value: 'Allows BYO',
                                Checked: !values.byo,
                                'SRP Version': 2,
                            });
                        }}
                    >
                        Allows BYO
                    </CheckBox>
                </FilterSection>
            )}
        </div>
    );
};
